import { actionTree } from "typed-vuex";
import { ActionContext } from "vuex";
import { StatusCodes } from "http-status-codes";
import { RegisterReq } from "@wl/types/lib/requests/auth/register.req";
import { LoginReq } from "@wl/types/lib/requests/auth/login.req";
import { ForgotReq } from "@wl/types/lib/requests/auth/forgot.req";
import { ResetReq } from "@wl/types/lib/requests/auth/reset.req";
import { MeReq } from "@wl/types/lib/requests/user/me.req";
import { LinkReq } from "@wl/types/lib/requests/link/link.req";
import { LinkTelegramReq } from "@wl/types/lib/requests/link/link.telegram.req";
import { Response, ResponseError } from "@wl/types/lib/responses";
import { LoginRes } from "@wl/types/lib/responses/auth/login.res";
import { ConformationRes } from "@wl/types/lib/responses/auth/conformation.res";
import { UserRes } from "@wl/types/lib/responses/user/user.res";
import { PostHog } from "posthog-js";
import { RootState } from "..";
import getters from "./getters";
import mutations from "./mutations";
import state, { UserState } from "./state";
import { ResetPasswordPayload } from ".";

type Me = Response<UserRes>;
type Login = Response<LoginRes>;
type ForgotPassword = Response<ConformationRes>;
type ResetPassword = Response<ConformationRes>;
type Context = ActionContext<UserState, RootState>;

/**
 * User module actions.
 */
const actions = actionTree(
    { state, getters, mutations },
    {
        /**
         * Fetches the currently logged in user.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async fetch(context: Context): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.get<Me>("/user/me");
            if (response.data.error) {
                if (response.status === StatusCodes.UNAUTHORIZED) this.app.$accessor.user.logout();

                return response.data.error;
            }

            this.app.$accessor.user.setUser(response.data.data);
        },

        /**
         * Updates the currently logged in user.
         *
         * @param {Context} context Action context
         * @param {MeReq} request Patch request
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async patch(context: Context, request: MeReq): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.patch<Me>("/user/me", request);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);

            await this.app.$accessor.fetch();
            ((this.app as any).$posthog as PostHog).capture("character_updated");
        },

        /**
         * Generated a random display name for the logged in user.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async generateRandomName(context: Context): Promise<ResponseError | undefined | string> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.get<Response<string>>("/user/random/name");
            if (response.data.error) return response.data.error;

            return response.data.data;
        },

        /**
         * Claims a access code.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async claimCode(context: Context): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.get<Me>("/codes/claim");
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            ((this.app as any).$posthog as PostHog).capture("claim_code");
        },

        /**
         * Claims Top 300 NFT.
         *
         * @param {Context} context Action context
         * @param {string} address Elrond wallet address to claim to
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async claimNFT(context: Context, address: string): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.get<Me>(`/nft/claim/${address}`);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            ((this.app as any).$posthog as PostHog).capture("claim_nft");
        },

        /**
         * Links a Discord account to currently logged in user.
         *
         * @param {Context} context Action context
         * @param {string} code OAuth code
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async linkDiscord(context: Context, code: string): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const request: LinkReq = { code };
            const response = await this.$axios.post<Me>("/link/discord", request);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_linked", { group_id: "discord" });
        },

        /**
         * Unlinks a Discord account from the currentlz logged in user.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async unlinkDiscord(context: Context): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.post<Me>("/unlink/discord");
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_unlinked", { group_id: "discord" });
        },

        /**
         * Links a Twitter account to currently logged in user.
         *
         * @param {Context} context Action context
         * @param {string} code OAuth code
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async linkTwitter(context: Context, code: string): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const request: LinkReq = { code };
            const response = await this.$axios.post<Me>("/link/twitter", request);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_linked", { group_id: "twitter" });
        },

        /**
         * Unlinks a Twitter account from the currently logged in user.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async unlinkTwitter(context: Context): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.post<Me>("/unlink/twitter");
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_unlinked", { group_id: "twitter" });
        },

        /**
         * Links a Telegram account to currently logged in user.
         *
         * @param {Context} context Action context
         * @param {LinkTelegramReq} req Telegram link request
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async linkTelegram(context: Context, req: LinkTelegramReq): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.post<Me>("/link/telegram", req);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_linked", { group_id: "telegram" });
        },

        /**
         * Unlinks a Telegram account from the currently logged in user.
         *
         * @param {Context} context Action context
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async unlinkTelegram(context: Context): Promise<ResponseError | undefined> {
            if (!context.state.token) return undefined;

            const response = await this.$axios.post<Me>("/unlink/telegram");
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setUser(response.data.data);
            await this.app.$accessor.action.fetch();
            await this.app.$accessor.ranking.fetch();
            ((this.app as any).$posthog as PostHog).capture("group_unlinked", { group_id: "telegram" });
        },

        /**
         * Registers a new user.
         *
         * @param {Context} _ Action context
         * @param {RegisterReq} request Register request
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async register(_: Context, request: RegisterReq): Promise<ResponseError | undefined> {
            const response = await this.$axios.post<Login>("/auth/register", request);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setToken(response.data.data?.accessToken);
        },

        /**
         * Logs in a existing user.
         *
         * @param {Context} _ Action context
         * @param {LoginReq} request Login request
         * @return {(Promise<ResponseError | undefined>)} Potential error
         */
        async login(_: Context, request: LoginReq): Promise<ResponseError | undefined> {
            const response = await this.$axios.post<Login>("/auth/login", request);
            if (response.data.error) return response.data.error;

            this.app.$accessor.user.setToken(response.data.data?.accessToken);
        },

        /**
         * Logs the user out.
         *
         * @param {Context} _ Action context
         */
        logout(_: Context): void {
            this.app.$accessor.clear();
            ((this.app as any).$posthog as PostHog).capture("waitinglist_logout");
            ((this.app as any).$posthog as PostHog).reset();
        },

        /**
         * Creates a reset password request when the user forgot the password.
         *
         * @param {Context} _ Action context
         * @param {ForgotReq} request Password forgot request
         * @return {(Promise<ResponseError | undefined>)}
         */
        async forgotPassword(_: Context, request: ForgotReq): Promise<ResponseError | undefined> {
            const response = await this.$axios.post<ForgotPassword>("/auth/password/forgot", request);
            if (response.data.error) return response.data.error;
        },

        /**
         * Resets the users password.
         *
         * @param {Context} _ Action context
         * @param {ResetPasswordPayload} payload Password reset payload
         * @return {(Promise<ResponseError | undefined>)}
         */
        async resetPassword(_: Context, payload: ResetPasswordPayload): Promise<ResponseError | undefined> {
            const request: ResetReq = { password: payload.password };
            const response = await this.$axios.post<ResetPassword>(`/auth/password/reset/${payload.token}`, request);
            if (response.data.error) return response.data.error;
        },
    },
);

export default actions;
